/**
 * 토스페이먼츠 v2 SDK 어댑터 — 퀵계좌이체(TRANSFER) 호출.
 *
 * ⚠️ Currently unused (sealed-vinyl 1차 phase에서 퀵계좌이체 UI 제외).
 * 다음 phase에서 PaymentStep에 'toss' 추가 시 재활성화 — 어댑터·콜백 API·types 모두 보존.
 *
 * customerKey는 Flutter bridge에서 받아옴 (시크릿 클라 노출 방지).
 * docs/payment/reference/sources/01-onetime-payment-implementation.html § 토스 퀵계좌이체 참조.
 */

import { TOSSPAYMENTS_V2_SDK_URL } from '../constants'

import { loadScript } from './loadScript'

interface TossPaymentRequest {
  amount: number
  orderId: string
  orderName: string
  successUrl: string
  failUrl: string
}

interface TossPaymentInstance {
  requestPayment: (params: {
    method: 'TRANSFER'
    amount: { value: number; currency: 'KRW' }
    orderId: string
    orderName: string
    successUrl: string
    failUrl: string
    transfer?: { useEscrow?: boolean }
  }) => Promise<void>
}

interface TossPaymentsInstance {
  payment: (params: { customerKey: string }) => TossPaymentInstance
}

declare global {
  interface Window {
    TossPayments?: (clientKey: string) => TossPaymentsInstance
  }
}

export async function openTossQuickAccount(
  clientKey: string,
  customerKey: string,
  request: TossPaymentRequest
): Promise<void> {
  if (!clientKey) throw new Error('TossPayments clientKey is missing')
  if (!customerKey) throw new Error('TossPayments customerKey is missing (bridge에서 받아와야 함)')

  await loadScript(TOSSPAYMENTS_V2_SDK_URL)
  if (!window.TossPayments) {
    throw new Error('TossPayments SDK not available on window')
  }
  const payment = window.TossPayments(clientKey).payment({ customerKey })
  await payment.requestPayment({
    method: 'TRANSFER',
    amount: { value: request.amount, currency: 'KRW' },
    orderId: request.orderId,
    orderName: request.orderName,
    successUrl: request.successUrl,
    failUrl: request.failUrl,
    transfer: { useEscrow: false },
  })
}

/** 토스 successUrl query parameters */
export interface TossSuccessQuery {
  paymentKey: string
  orderId: string
  amount: number
}

/** 토스 failUrl query parameters */
export interface TossFailQuery {
  code: string
  message: string
  orderId: string
}

export function parseTossSuccessQuery(search: URLSearchParams): TossSuccessQuery | null {
  const paymentKey = search.get('paymentKey')
  const orderId = search.get('orderId')
  const amountStr = search.get('amount')
  if (!paymentKey || !orderId || !amountStr) return null
  const amount = Number(amountStr)
  if (Number.isNaN(amount)) return null
  return { paymentKey, orderId, amount }
}

export function parseTossFailQuery(search: URLSearchParams): TossFailQuery | null {
  const code = search.get('code')
  const message = search.get('message')
  const orderId = search.get('orderId')
  if (!code || !message || !orderId) return null
  return { code, message, orderId }
}
